<template>
  <div>
    <div v-if="loading" class="flex flex-col items-center justify-center py-24 text-slate-400">
      <div class="h-8 w-8 rounded-full border-2 border-slate-200 border-t-brand animate-spin"></div>
      <p class="mt-3 text-sm">Yükleniyor…</p>
    </div>

    <div v-else-if="notFound" class="text-center py-24">
      <div class="text-5xl mb-3">🔍</div>
      <p class="text-slate-500">Geçersiz veya süresi dolmuş bağlantı.</p>
    </div>

    <!-- Şifre ekranı -->
    <div
      v-else-if="!authed"
      class="max-w-sm mx-auto mt-16 rounded-3xl bg-white shadow-sm border border-slate-100 p-7"
    >
      <div class="text-center">
        <div class="mx-auto h-14 w-14 rounded-2xl bg-brand/10 flex items-center justify-center text-2xl">
          📊
        </div>
        <h1 class="text-lg font-bold text-slate-800 mt-4">Marka Raporu</h1>
        <p class="text-sm text-slate-500 mt-1">Devam etmek için şifreni gir.</p>
      </div>
      <form @submit.prevent="doAuth" class="mt-6 space-y-3">
        <input
          v-model="password"
          type="password"
          placeholder="Şifre"
          autofocus
          class="w-full rounded-xl border border-slate-200 py-2.5 px-3.5 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition"
        />
        <button
          :disabled="!password || authBusy"
          type="submit"
          class="w-full rounded-xl bg-brand hover:bg-brand-dark disabled:opacity-40 text-white font-semibold py-3 transition shadow-sm shadow-brand/30"
        >
          {{ authBusy ? "Kontrol ediliyor…" : "Giriş" }}
        </button>
        <p v-if="authError" class="text-sm text-red-500 text-center">{{ authError }}</p>
      </form>
    </div>

    <template v-else>
      <!-- Başlık -->
      <div class="rounded-3xl bg-white shadow-sm border border-slate-100 p-6 sm:p-7">
        <div class="flex items-center gap-3">
          <img
            v-if="data.companyLogoUrl"
            :src="data.companyLogoUrl"
            :alt="data.companyName"
            class="rounded-2xl object-contain bg-slate-50"
            style="width: 44px; height: 44px; flex: none"
          />
          <span
            v-else
            class="rounded-2xl bg-brand/10 flex items-center justify-center text-xl"
            style="width: 44px; height: 44px; flex: none"
            >🏢</span
          >
          <div class="flex-1 min-w-0">
            <p class="text-xs font-medium text-brand uppercase tracking-wide">
              İçerik kampanyası raporu
            </p>
            <h1 class="text-lg font-bold text-slate-800 leading-tight">
              {{ data.companyName }}
            </h1>
          </div>
          <button
            class="text-xs text-slate-400 hover:text-slate-600 transition"
            title="Verileri yenile"
            @click="load()"
          >
            ⟳ Yenile
          </button>
        </div>
      </div>

      <!-- Özet kartları -->
      <section class="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div
          v-for="kpi in kpis"
          :key="kpi.label"
          class="rounded-2xl bg-white shadow-sm border border-slate-100 p-4"
        >
          <p class="text-xs font-medium text-slate-400">{{ kpi.label }}</p>
          <p class="text-2xl font-extrabold text-slate-800 mt-1">{{ kpi.value }}</p>
          <p v-if="kpi.hint" class="text-xs text-slate-400 mt-0.5">{{ kpi.hint }}</p>
        </div>
      </section>

      <!-- Genel süreç dağılımı -->
      <section class="mt-5 rounded-3xl bg-white shadow-sm border border-slate-100 p-6 sm:p-7">
        <div class="flex items-baseline justify-between mb-4">
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide">
            Genel süreç dağılımı
          </p>
          <p class="text-xs text-slate-400">
            {{ summary.completedCount }} / {{ summary.influencerCount }} tamamlandı
          </p>
        </div>

        <div class="flex h-3 w-full rounded-full overflow-hidden bg-slate-100">
          <div
            v-for="s in statusBars(summary.status, summary.influencerCount)"
            :key="s.key"
            :class="s.bg"
            :style="{ width: s.width }"
            :title="`${s.label}: ${s.count}`"
          ></div>
        </div>

        <div class="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div v-for="s in statusBars(summary.status, summary.influencerCount)" :key="s.key">
            <div class="flex items-center gap-1.5">
              <span :class="['h-2 w-2 rounded-full', s.bg]"></span>
              <span class="text-xs text-slate-500">{{ s.label }}</span>
            </div>
            <p class="text-lg font-bold text-slate-800 leading-tight">{{ s.count }}</p>
          </div>
        </div>
      </section>

      <!-- Kampanyalar -->
      <section class="mt-5">
        <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3 px-1">
          Kampanyalar ({{ campaigns.length }})
        </p>

        <div v-if="!campaigns.length" class="rounded-3xl bg-white border border-slate-100 p-10 text-center">
          <p class="text-slate-500">Henüz bir içerik kampanyası bulunmuyor.</p>
        </div>

        <div
          v-for="c in campaigns"
          :key="c.id"
          class="rounded-3xl bg-white shadow-sm border border-slate-100 p-5 sm:p-6 mb-4"
        >
          <!-- Kampanya başlığı -->
          <div class="flex items-start gap-3 flex-wrap">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <h2 class="text-base font-bold text-slate-800">{{ c.name }}</h2>
                <span
                  :class="[
                    'text-xs font-semibold px-2 py-0.5 rounded-full',
                    c.isActive
                      ? 'bg-emerald-50 text-emerald-600'
                      : 'bg-slate-100 text-slate-500',
                  ]"
                  >{{ c.isActive ? "Aktif" : "Tamamlandı" }}</span
                >
              </div>
              <p class="text-xs text-slate-400 mt-1">
                {{ formatDate(c.startDate) }}
                <template v-if="c.endDate"> – {{ formatDate(c.endDate) }}</template>
                <template v-if="c.publishDate">
                  · Yayın: {{ formatDate(c.publishDate) }}</template
                >
              </p>
            </div>
            <div class="text-right">
              <p class="text-2xl font-extrabold text-slate-800 leading-none">
                %{{ c.completionRate }}
              </p>
              <p class="text-xs text-slate-400 mt-1">tamamlanma</p>
            </div>
          </div>

          <!-- Kampanya dağılımı -->
          <div class="mt-4 flex h-2.5 w-full rounded-full overflow-hidden bg-slate-100">
            <div
              v-for="s in statusBars(c.status, c.influencerCount)"
              :key="s.key"
              :class="s.bg"
              :style="{ width: s.width }"
              :title="`${s.label}: ${s.count}`"
            ></div>
          </div>

          <div class="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
            <div
              v-for="s in statusBars(c.status, c.influencerCount)"
              :key="s.key"
              class="flex items-center gap-1.5"
            >
              <span :class="['h-2 w-2 rounded-full', s.bg]"></span>
              <span class="text-xs text-slate-500">{{ s.label }}</span>
              <span class="text-xs font-bold text-slate-700">{{ s.count }}</span>
            </div>
          </div>

          <!-- Sekmeler -->
          <div class="mt-5 flex items-center gap-1 border-b border-slate-100">
            <button
              v-for="tab in tabsFor(c)"
              :key="tab.key"
              :class="[
                'text-sm font-semibold px-3 py-2 -mb-px border-b-2 transition',
                activeTab(c.id) === tab.key
                  ? 'border-brand text-brand'
                  : 'border-transparent text-slate-400 hover:text-slate-600',
              ]"
              @click="setTab(c.id, tab.key)"
            >
              {{ tab.label }}
            </button>
          </div>

          <!-- Katılımcılar (anonim) -->
          <div v-if="activeTab(c.id) === 'rows'" class="mt-3">
            <p class="text-xs text-slate-400 mb-3">
              Katılımcı kimlikleri gizlilik gereği maskelenmiştir.
            </p>
            <div v-if="!c.influencers.length" class="text-sm text-slate-400 py-6 text-center">
              Bu kampanyaya henüz katılım yok.
            </div>
            <div v-else class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="text-left text-xs text-slate-400 border-b border-slate-100">
                    <th class="py-2 pr-3 font-medium">Katılımcı</th>
                    <th class="py-2 pr-3 font-medium">Durum</th>
                    <th class="py-2 pr-3 font-medium whitespace-nowrap">Taslak</th>
                    <th class="py-2 pr-3 font-medium whitespace-nowrap">Onay</th>
                    <th class="py-2 pr-3 font-medium whitespace-nowrap">Yayın</th>
                    <th class="py-2 font-medium whitespace-nowrap">İstatistik</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(i, idx) in c.influencers"
                    :key="idx"
                    class="border-b border-slate-50 last:border-0"
                  >
                    <td class="py-2.5 pr-3 font-mono text-slate-500 whitespace-nowrap">
                      {{ i.maskedHandle }}
                    </td>
                    <td class="py-2.5 pr-3">
                      <span
                        :class="[
                          'text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap',
                          statusChip(i.status),
                        ]"
                        >{{ statusText(i.status) }}</span
                      >
                    </td>
                    <td class="py-2.5 pr-3 text-slate-400 whitespace-nowrap">
                      {{ shortDate(i.draftSubmittedAt) }}
                    </td>
                    <td class="py-2.5 pr-3 text-slate-400 whitespace-nowrap">
                      {{ shortDate(i.approvedAt) }}
                    </td>
                    <td class="py-2.5 pr-3 text-slate-400 whitespace-nowrap">
                      {{ shortDate(i.postSubmittedAt) }}
                    </td>
                    <td class="py-2.5 text-slate-400 whitespace-nowrap">
                      {{ shortDate(i.statsSubmittedAt) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Yayınlanan içerikler -->
          <div v-else class="mt-3">
            <div v-if="!c.posts.length" class="text-sm text-slate-400 py-6 text-center">
              Henüz yayınlanan içerik yok.
            </div>
            <ul v-else class="divide-y divide-slate-50">
              <li
                v-for="(p, idx) in c.posts"
                :key="idx"
                class="py-2.5 flex items-center gap-3 flex-wrap"
              >
                <span class="font-mono text-xs text-slate-400 whitespace-nowrap">{{
                  p.maskedHandle
                }}</span>
                <a
                  :href="p.postUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-brand hover:text-brand-dark font-medium truncate flex-1 min-w-0"
                  >{{ p.postUrl }}</a
                >
                <span
                  v-if="p.statsReceived"
                  class="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 whitespace-nowrap"
                  >İstatistik alındı</span
                >
                <span class="text-xs text-slate-400 whitespace-nowrap">{{
                  shortDate(p.publishedAt)
                }}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <p class="text-center text-xs text-slate-400 mt-6">
        Bu rapor yalnızca özet verileri içerir. Detaylı içerik ve katılımcı bilgileri için
        operasyon ekibinizle iletişime geçin.
      </p>
    </template>
  </div>
</template>

<script lang="ts">
import { Vue, Component } from "vue-property-decorator";
import api from "./api";

// ContentInfluencerStatus sırasıyla eşleşir (0..5). Sınıf içinde static alan
// olarak tutulamıyor: mevcut Babel yapılandırması static class block desteklemiyor.
const STATUSES = [
  { key: "registered", label: "Kayıt", bg: "bg-slate-300" },
  { key: "draftSubmitted", label: "Taslak", bg: "bg-amber-400" },
  { key: "revisionRequested", label: "Revizyon", bg: "bg-rose-400" },
  { key: "approved", label: "Onaylandı", bg: "bg-sky-400" },
  { key: "postSubmitted", label: "Yayında", bg: "bg-violet-500" },
  { key: "statsSubmitted", label: "Tamamlandı", bg: "bg-emerald-500" },
];

const STATUS_CHIPS = [
  "bg-slate-100 text-slate-500",
  "bg-amber-50 text-amber-600",
  "bg-rose-50 text-rose-600",
  "bg-sky-50 text-sky-600",
  "bg-violet-50 text-violet-600",
  "bg-emerald-50 text-emerald-600",
];

@Component
export default class BrandPortal extends Vue {
  loading = true;
  notFound = false;
  authed = false;
  authBusy = false;
  authError = "";
  password = "";
  sessionToken = "";

  data: any = {};
  // Kampanya id -> açık sekme ("rows" | "posts")
  tabs: { [id: string]: string } = {};

  get token(): string {
    return this.$route.params.token;
  }

  get sessionKey(): string {
    return `brand-session-${this.token}`;
  }

  get summary(): any {
    return this.data.summary || { status: {} };
  }

  get campaigns(): any[] {
    return this.data.campaigns || [];
  }

  get kpis() {
    const s = this.summary;
    return [
      {
        label: "Kampanya",
        value: s.campaignCount || 0,
        hint: `${s.activeCampaignCount || 0} aktif`,
      },
      { label: "Toplam katılımcı", value: s.influencerCount || 0, hint: "" },
      {
        label: "Yayınlanan içerik",
        value: s.publishedCount || 0,
        hint: "gönderi linki girilen",
      },
      {
        label: "Tamamlanma",
        value: `%${s.completionRate || 0}`,
        hint: `${s.completedCount || 0} süreç kapandı`,
      },
    ];
  }

  async mounted() {
    const saved = sessionStorage.getItem(this.sessionKey);
    if (saved) {
      this.sessionToken = saved;
      await this.load();
    } else {
      this.loading = false;
    }
  }

  async doAuth() {
    this.authBusy = true;
    this.authError = "";
    try {
      const res = await api.postJson(`/api/BrandPortal/${this.token}/auth`, {
        password: this.password,
      });
      this.sessionToken = res.sessionToken;
      sessionStorage.setItem(this.sessionKey, res.sessionToken);
      await this.load();
    } catch {
      this.authError = "Şifre hatalı.";
    }
    this.authBusy = false;
  }

  async load() {
    try {
      this.data = await api.getJsonBrand(`/api/BrandPortal/${this.token}`, this.sessionToken);
      this.authed = true;
    } catch (e) {
      // 401 → şifre değişmiş ya da oturum geçersiz; kullanıcıyı şifre ekranına al.
      if ((e as Error).message === "401") {
        sessionStorage.removeItem(this.sessionKey);
        this.authed = false;
      } else {
        this.notFound = true;
      }
    }
    this.loading = false;
  }

  // ---- Sekmeler ----

  tabsFor(c: any) {
    return [
      { key: "rows", label: `Katılımcılar (${(c.influencers || []).length})` },
      { key: "posts", label: `Yayınlanan içerikler (${(c.posts || []).length})` },
    ];
  }

  activeTab(id: string): string {
    return this.tabs[id] || "rows";
  }

  setTab(id: string, key: string) {
    this.$set(this.tabs, id, key);
  }

  // ---- Durum görselleştirme ----

  statusBars(status: any, total: number) {
    const s = status || {};
    return STATUSES.map((def) => {
      const count = s[def.key] || 0;
      return {
        ...def,
        count,
        width: total > 0 ? `${(count * 100) / total}%` : "0%",
      };
    });
  }

  statusText(status: number): string {
    const def = STATUSES[status];
    return def ? def.label : "—";
  }

  statusChip(status: number): string {
    return STATUS_CHIPS[status] || STATUS_CHIPS[0];
  }

  // ---- Tarih ----

  formatDate(value: string): string {
    if (!value) return "—";
    const d = new Date(value);
    return d.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  shortDate(value: string): string {
    if (!value) return "—";
    const d = new Date(value);
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" });
  }
}
</script>
